import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAccount, useConnect, useDisconnect, useWriteContract, useWaitForTransactionReceipt, useSwitchChain, useBalance } from 'wagmi';
import { parseUnits } from 'viem';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Loader2, CheckCircle, XCircle, ExternalLink, Wallet, AlertCircle, ArrowLeft, Copy, ArrowRight, HelpCircle, RefreshCw, ChevronDown, ChevronUp, Shield, QrCode } from 'lucide-react';
import { isWalletConnectAvailable } from '../config/wagmi';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import PWAInstallBanner from '../components/PWAInstallBanner';
import { usePWAReconnect, setPWAConnecting } from '../hooks/usePWAReconnect';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const BSC_CHAIN_ID = 56;

// TIP Token on BSC
const TIP_TOKEN_ADDRESS = process.env.REACT_APP_TIP_TOKEN_ADDRESS || '0x45D8e90deFB7a88C3e5584043205da5a90a43A34';
const TIP_TOKEN_ABI = [
  {
    "inputs": [{ "name": "to", "type": "address" }, { "name": "amount", "type": "uint256" }],
    "name": "transfer",
    "outputs": [{ "name": "", "type": "bool" }],
    "stateMutability": "nonpayable",
    "type": "function"
  }
];

// Minimum balances
const MIN_BNB_FOR_GAS = 0.001; // ~$0.50 for gas
const MIN_TIP_TO_SEND = 1;

// Wallet deep links
const DEEP_LINKS = {
  metamask: (url) => `https://metamask.app.link/dapp/${url.replace(/^https?:\/\//, '')}`,
  trust: (url) => `https://link.trustwallet.com/open_url?coin_id=56&url=${encodeURIComponent(url)}`,
  rabby: (url) => `https://rabby.io` // Rabby doesn't have deep links, just link to site
};

// Wallet info for install screen
const WALLET_INFO = {
  metamask: {
    name: 'MetaMask',
    icon: '🦊',
    recommended: true,
    description: 'Most popular wallet. Easiest for first-time users.',
    hasDeepLink: true
  },
  trust: {
    name: 'Trust Wallet',
    icon: '🛡️',
    recommended: false,
    description: 'Simple mobile wallet.',
    hasDeepLink: true
  },
  rabby: {
    name: 'Rabby',
    icon: '🐰',
    recommended: false,
    description: 'For experienced Web3 users.',
    hasDeepLink: false,
    advanced: true
  }
};

// Detection helpers
const detectWalletInstalled = () => {
  if (typeof window === 'undefined') return false;
  return Boolean(window.ethereum);
};

const isInsideWalletBrowser = () => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return (window.ethereum?.isMetaMask && /MetaMaskMobile/i.test(ua)) ||
         window.ethereum?.isTrust || /Trust/i.test(ua) ||
         window.ethereum?.isRabby ||
         (window.ethereum && /Mobile|Android|iPhone/i.test(ua));
};

const isMobile = () => /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent || '');

// localStorage keys
const STORAGE_KEYS = {
  introSeen: 'tipme_wallet_intro_seen',
  installStarted: 'tipme_wallet_install_started',
  userMode: 'tipme_user_mode' // 'advanced' | 'beginner'
};

// PancakeSwap V2 Router for inline swaps
const PANCAKE_ROUTER_ADDRESS = '0x10ED43C718714eb63d5aA57B78B54704E256024E';
const WBNB_ADDRESS = '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c';
const DEFAULT_SWAP_BNB = '0.01'; // Default ~$5 worth
const DEFAULT_SLIPPAGE = 5; // 5% default slippage
const SWAP_DEADLINE_MINUTES = 20;

// Auto-redirect after delay (used on success screen)
function AutoRedirect({ to, delay, navigate }) {
  React.useEffect(() => {
    const timer = setTimeout(() => navigate(to, { replace: true }), delay);
    return () => clearTimeout(timer);
  }, [to, delay, navigate]);
  return null;
}

const PANCAKE_ROUTER_ABI = [
  {
    "inputs": [
      { "name": "amountOutMin", "type": "uint256" },
      { "name": "path", "type": "address[]" },
      { "name": "to", "type": "address" },
      { "name": "deadline", "type": "uint256" }
    ],
    "name": "swapExactETHForTokens",
    "outputs": [{ "name": "amounts", "type": "uint256[]" }],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      { "name": "amountIn", "type": "uint256" },
      { "name": "path", "type": "address[]" }
    ],
    "name": "getAmountsOut",
    "outputs": [{ "name": "amounts", "type": "uint256[]" }],
    "stateMutability": "view",
    "type": "function"
  }
];

// Wallet browser context detection
const getWalletBrowserContext = () => {
  if (typeof window === 'undefined') return null;
  
  const ua = navigator.userAgent || '';
  const eth = window.ethereum;
  
  // Inside MetaMask Mobile
  if (eth?.isMetaMask && /MetaMaskMobile/i.test(ua)) {
    return {
      insideWallet: true,
      walletName: 'MetaMask',
      message: "You're inside the MetaMask browser. This lets you approve the tip securely."
    };
  }
  
  // Inside Trust Wallet
  if (eth?.isTrust || /Trust/i.test(ua)) {
    return {
      insideWallet: true,
      walletName: 'Trust Wallet',
      message: "You're inside Trust Wallet. This lets you approve the tip securely."
    };
  }
  
  // Inside Rabby
  if (eth?.isRabby) {
    return {
      insideWallet: true,
      walletName: 'Rabby',
      message: "You're inside Rabby. This lets you approve the tip securely."
    };
  }
  
  // Has wallet but NOT inside wallet browser (mobile only)
  if (eth && isMobile()) {
    return {
      insideWallet: false,
      walletName: null,
      message: "Open this page inside your wallet app to continue."
    };
  }
  
  return null;
};

// ============================================================================
// GRANDMOTHER-FRIENDLY ONBOARDING COMPONENTS
// ============================================================================

// Status indicator component
const StatusBadge = ({ ready, label }) => (
  <div className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium ${
    ready 
      ? 'bg-green-100 text-green-700 border border-green-200' 
      : 'bg-amber-100 text-amber-700 border border-amber-200'
  }`}>
    {ready ? (
      <CheckCircle className="h-4 w-4" />
    ) : (
      <AlertCircle className="h-4 w-4" />
    )}
    {label}
  </div>
);

// Simple expandable help section
const HelpSection = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-[#b8b3ab] rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-[#e8e3db] hover:bg-[#ddd8d0] transition-colors text-left"
      >
        <span className="font-semibold text-[#1a1a1a]">{title}</span>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-[#666]" />
        ) : (
          <ChevronDown className="h-5 w-5 text-[#666]" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 bg-white border-t border-[#b8b3ab]">
          {children}
        </div>
      )}
    </div>
  );
};

// Main onboarding guide - shows exactly what's needed (with inline swap)
const OnboardingGuide = ({ hasBNB, hasTIP, onRefresh, isRefreshing, address, onSwapComplete }) => {
  const [copied, setCopied] = useState(false);
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [swapAmount, setSwapAmount] = useState(DEFAULT_SWAP_BNB);
  const [slippage, setSlippage] = useState(DEFAULT_SLIPPAGE);
  const [swapQuote, setSwapQuote] = useState(null);
  const [swapLoading, setSwapLoading] = useState(false);
  const [swapError, setSwapError] = useState(null);
  const [lastCheckFailed, setLastCheckFailed] = useState(false);
  
  const copyAddress = () => {
    navigator.clipboard.writeText(TIP_TOKEN_ADDRESS);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  // Handle refresh with feedback tracking
  const handleRefreshWithFeedback = async () => {
    setLastCheckFailed(false);
    await onRefresh();
    // If we're still rendered (still need onboarding), show the "still missing" feedback
    // This is a small delay to let the parent re-render if balances are good
    setTimeout(() => {
      setLastCheckFailed(true);
    }, 1500);
  };

  // Determine what the user needs
  const needsBNB = !hasBNB;
  const needsTIP = !hasTIP;
  
  // Get swap quote when modal opens or amount changes
  const getSwapQuote = useCallback(async () => {
    if (!swapAmount || parseFloat(swapAmount) <= 0) return;
    
    try {
      setSwapLoading(true);
      setSwapError(null);
      
      // Simple estimate: Use a rough TIP/BNB ratio
      // In production, this would call the router's getAmountsOut
      const bnbPrice = 600; // Rough USD price
      const tipPrice = 0.10; // Rough USD price  
      const bnbValue = parseFloat(swapAmount) * bnbPrice;
      const estimatedTip = Math.floor(bnbValue / tipPrice * 0.95); // 5% slippage buffer
      
      setSwapQuote({
        inputAmount: swapAmount,
        outputAmount: estimatedTip,
        priceImpact: '< 1%'
      });
    } catch (err) {
      setSwapError('Could not get quote');
    } finally {
      setSwapLoading(false);
    }
  }, [swapAmount]);
  
  useEffect(() => {
    if (showSwapModal) {
      getSwapQuote();
    }
  }, [showSwapModal, swapAmount, getSwapQuote]);
  
  // Execute swap via wallet
  const executeSwap = async () => {
    try {
      setSwapLoading(true);
      setSwapError(null);
      
      // This triggers the wallet's native transaction approval
      const amountInWei = BigInt(Math.floor(parseFloat(swapAmount) * 1e18));
      const deadline = Math.floor(Date.now() / 1000) + SWAP_DEADLINE_MINUTES * 60;
      const path = [WBNB_ADDRESS, TIP_TOKEN_ADDRESS];
      
      // Calculate minimum output with slippage
      const minOutput = BigInt(Math.floor(swapQuote.outputAmount * (100 - slippage) / 100 * 1e18));
      
      // The actual swap would be executed here via wagmi's useWriteContract
      // For now, we'll open PancakeSwap with pre-filled params as fallback
      const swapUrl = `https://pancakeswap.finance/swap?inputCurrency=BNB&outputCurrency=${TIP_TOKEN_ADDRESS}&exactAmount=${swapAmount}&exactField=input`;
      
      // Open in same window to stay in wallet browser context
      window.location.href = swapUrl;
      
    } catch (err) {
      setSwapError(err.message || 'Swap failed');
      setSwapLoading(false);
    }
  };
  
  return (
    <Card className="w-full max-w-md bg-[#e8e3db] border-[#b8b3ab] p-6" data-testid="onboarding-guide">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-[#f0b90b]/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <HelpCircle className="h-8 w-8 text-[#f0b90b]" />
        </div>
        <h2 className="text-xl font-bold text-[#1a1a1a] mb-2">Almost Ready to Tip!</h2>
        <p className="text-[#666] text-sm">
          You need a couple of things before you can send a tip.
        </p>
      </div>

      {/* Status Overview */}
      <div className="flex flex-wrap gap-2 justify-center mb-6">
        <StatusBadge ready={hasBNB} label={hasBNB ? "Transaction fee ready" : "Need transaction fee"} />
        <StatusBadge ready={hasTIP} label={hasTIP ? "TIP tokens ready" : "Need TIP tokens"} />
      </div>

      {/* What You Need - Simple explanation */}
      <div className="space-y-4 mb-6">
        
        {/* Step 1: BNB for gas (if needed) */}
        {needsBNB && (
          <HelpSection title="Step 1: Get a small transaction fee" defaultOpen={true}>
            <div className="space-y-3">
              <p className="text-[#666] text-sm leading-relaxed">
                <strong>What is this?</strong> Every digital payment needs a tiny fee (less than $0.10) to process. 
                This fee is paid in "BNB" - think of it like paying postage to mail a letter.
              </p>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-gray-800 text-sm font-medium mb-2">How to get BNB:</p>
                <ol className="text-gray-600 text-sm space-y-1 list-decimal list-inside">
                  <li>Visit a crypto exchange like Binance or Coinbase</li>
                  <li>Create an account (you'll need to verify your identity)</li>
                  <li>Buy $5-10 worth of BNB</li>
                  <li>Send it to your wallet address</li>
                </ol>
                <a
                  href="https://www.binance.com/en/how-to-buy/bnb"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[#f0b90b] hover:underline text-sm font-semibold mt-2"
                >
                  Learn how to buy BNB <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          </HelpSection>
        )}

        {/* Step 2: TIP tokens (if needed) */}
        {needsTIP && (
          <HelpSection title={needsBNB ? "Step 2: Get TIP tokens" : "Get TIP tokens"} defaultOpen={!needsBNB}>
            <div className="space-y-3">
              <p className="text-[#666] text-sm leading-relaxed">
                <strong>What are TIP tokens?</strong> TIP is the currency used for tipping in this app. 
                You'll swap some of your BNB for TIP tokens.
              </p>

              <Button 
                onClick={() => setShowSwapModal(true)}
                className="w-full bg-[#f0b90b] hover:bg-[#d4a50a] text-white" 
                data-testid="swap-tip-btn"
                disabled={needsBNB}
              >
                {needsBNB ? 'Get BNB first, then swap for TIP' : 'Swap BNB for TIP'}
              </Button>
              
              {needsBNB && (
                <p className="text-xs text-[#888] text-center">
                  Complete Step 1 first to enable swapping
                </p>
              )}

              {/* Advanced: Contract address (collapsed by default) */}
              <details className="text-sm">
                <summary className="text-[#666] cursor-pointer hover:text-[#1a1a1a]">
                  Advanced: TIP token contract address
                </summary>
                <div className="mt-2 bg-[#1a1a1a] rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <code className="text-[#f0b90b] text-xs flex-1 break-all font-mono">
                      {TIP_TOKEN_ADDRESS}
                    </code>
                    <button
                      onClick={copyAddress}
                      className="p-2 hover:bg-gray-700 rounded transition-colors"
                      data-testid="copy-address-btn"
                    >
                      {copied ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              </details>
            </div>
          </HelpSection>
        )}
      </div>

      {/* Refresh Button */}
      <Button
        onClick={handleRefreshWithFeedback}
        disabled={isRefreshing}
        className="w-full bg-[#22c55e] hover:bg-[#16a34a] text-white"
        data-testid="check-balance-btn"
      >
        {isRefreshing ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Checking...
          </>
        ) : (
          <>
            <RefreshCw className="h-4 w-4 mr-2" />
            I'm ready - check my balance
          </>
        )}
      </Button>
      
      {/* Feedback when balance check shows still missing tokens */}
      {lastCheckFailed && !isRefreshing && (needsBNB || needsTIP) && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="balance-feedback">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="text-amber-700 font-medium mb-1">
                {needsBNB && needsTIP && "Still missing BNB and TIP tokens"}
                {needsBNB && !needsTIP && "Still missing BNB for transaction fee"}
                {!needsBNB && needsTIP && "Still missing TIP tokens"}
              </p>
              <p className="text-amber-600 text-xs">
                {needsBNB 
                  ? "After buying BNB on an exchange, it may take 1-5 minutes to arrive in your wallet."
                  : "After swapping for TIP, it may take a moment to appear. Try checking again in a minute."}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Inline Swap Modal */}
      {showSwapModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="swap-modal">
          <Card className="w-full max-w-sm bg-[#e8e3db] border-[#b8b3ab] p-6">
            <h3 className="text-lg font-bold text-[#1a1a1a] text-center mb-4">
              Swap BNB for TIP
            </h3>
            
            {/* Amount Input */}
            <div className="mb-4">
              <label className="block text-sm text-[#666] mb-1">Amount to swap (BNB)</label>
              <Input
                type="number"
                value={swapAmount}
                onChange={(e) => setSwapAmount(e.target.value)}
                step="0.001"
                min="0.001"
                className="w-full"
                data-testid="swap-amount-input"
              />
              <div className="flex gap-2 mt-2">
                {['0.005', '0.01', '0.02', '0.05'].map(amt => (
                  <button
                    key={amt}
                    onClick={() => setSwapAmount(amt)}
                    className={`flex-1 py-1 text-xs rounded border ${swapAmount === amt ? 'bg-[#f0b90b] text-white border-[#f0b90b]' : 'bg-white border-[#b8b3ab] text-[#666]'}`}
                  >
                    {amt}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Slippage Setting */}
            <div className="mb-4">
              <label className="block text-sm text-[#666] mb-1">Slippage tolerance (%)</label>
              <div className="flex gap-2">
                {[1, 3, 5, 10].map(s => (
                  <button
                    key={s}
                    onClick={() => setSlippage(s)}
                    className={`flex-1 py-2 text-sm rounded border ${slippage === s ? 'bg-[#f0b90b] text-white border-[#f0b90b]' : 'bg-white border-[#b8b3ab] text-[#666]'}`}
                  >
                    {s}%
                  </button>
                ))}
              </div>
            </div>
            
            {/* Quote Preview */}
            {swapQuote && !swapLoading && (
              <div className="bg-white rounded-lg p-3 border border-[#b8b3ab] mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[#666]">You pay</span>
                  <span className="font-semibold">{swapQuote.inputAmount} BNB</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[#666]">You receive (est.)</span>
                  <span className="font-semibold text-green-600">~{swapQuote.outputAmount} TIP</span>
                </div>
                <div className="flex justify-between text-xs text-[#888]">
                  <span>Network fee</span>
                  <span>~$0.10</span>
                </div>
              </div>
            )}
            
            {swapLoading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-[#f0b90b]" />
              </div>
            )}
            
            {swapError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-red-700 text-sm">
                {swapError}
              </div>
            )}
            
            {/* Actions */}
            <Button
              onClick={executeSwap}
              disabled={swapLoading || !swapQuote}
              className="w-full h-12 bg-[#f0b90b] hover:bg-[#d4a50a] text-white mb-3"
              data-testid="confirm-swap-btn"
            >
              {swapLoading ? 'Processing...' : 'Confirm Swap'}
            </Button>
            
            <Button
              onClick={() => setShowSwapModal(false)}
              variant="outline"
              className="w-full h-12 border-[#b8b3ab]"
            >
              Cancel
            </Button>
            
            <p className="text-xs text-[#888] text-center mt-3">
              Swap powered by PancakeSwap. Prices are estimates.
            </p>
          </Card>
        </div>
      )}
    </Card>
  );
};

// ============================================================================
// NOOB FLOW SCREENS - For first-time mobile users without wallets
// ============================================================================

// Screen Header - Shows who they're tipping (keeps context)
const NoobFlowHeader = ({ creatorLabel, walletAddress }) => (
  <div className="text-center mb-4 pb-4 border-b border-[#b8b3ab]">
    <p className="text-sm text-[#666]">You're tipping</p>
    <p className="font-bold text-[#1a1a1a]">{creatorLabel || 'Creator'}</p>
    <p className="font-mono text-xs text-[#999]">{walletAddress?.slice(0, 6)}...{walletAddress?.slice(-4)}</p>
  </div>
);

// Wallet Browser Context Banner - Shows contextual message about wallet browser
const WalletContextBanner = () => {
  const context = getWalletBrowserContext();
  if (!context) return null;
  
  return (
    <div className={`text-center text-sm py-2 px-4 rounded-lg mb-4 ${
      context.insideWallet 
        ? 'bg-green-50 text-green-700 border border-green-200' 
        : 'bg-amber-50 text-amber-700 border border-amber-200'
    }`} data-testid="wallet-context-banner">
      {context.insideWallet && <span className="mr-1">✓</span>}
      {context.message}
    </div>
  );
};

// Entry Choice Screen - First screen for mobile users (Advanced vs Beginner)
const EntryChoiceScreen = ({ creatorLabel, walletAddress, onQuickTip, onNoob, rememberChoice, setRememberChoice }) => (
  <div className="min-h-screen bg-[#d4cfc4] flex items-center justify-center p-4">
    <Card className="w-full max-w-md bg-[#e8e3db] border-[#b8b3ab] p-6" data-testid="entry-choice-screen">
      {/* Header */}
      <div className="text-center mb-6 pb-4 border-b border-[#b8b3ab]">
        <div className="w-14 h-14 bg-[#f0b90b]/20 rounded-full flex items-center justify-center mx-auto mb-3">
          <Wallet className="h-7 w-7 text-[#f0b90b]" />
        </div>
        <p className="text-sm text-[#666] mb-1">You're about to tip</p>
        <p className="font-bold text-[#1a1a1a] text-lg">{creatorLabel || 'Creator'}</p>
        <p className="font-mono text-xs text-[#999]">{walletAddress?.slice(0, 6)}...{walletAddress?.slice(-4)}</p>
      </div>
      
      <h2 className="text-xl font-bold text-[#1a1a1a] text-center mb-6">
        How would you like to continue?
      </h2>
      
      {/* Option 1: Quick Tip (Advanced) */}
      <button
        onClick={onQuickTip}
        className="w-full bg-[#f0b90b] hover:bg-[#d4a50a] text-white rounded-xl p-4 mb-3 text-left transition-all flex items-center justify-between group"
        data-testid="entry-quick-tip"
      >
        <div>
          <p className="font-bold text-lg">Quick Tip</p>
          <p className="text-white/80 text-sm">I already use crypto</p>
        </div>
        <ArrowRight className="h-5 w-5 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
      </button>
      
      {/* Option 2: I'm a Noob (Beginner) */}
      <button
        onClick={onNoob}
        className="w-full bg-white hover:bg-gray-50 border-2 border-[#b8b3ab] hover:border-[#f0b90b] rounded-xl p-4 mb-6 text-left transition-all flex items-center justify-between group"
        data-testid="entry-noob"
      >
        <div>
          <p className="font-bold text-[#1a1a1a] text-lg">I'm a Noob</p>
          <p className="text-[#666] text-sm">Guide me step by step</p>
        </div>
        <ArrowRight className="h-5 w-5 text-[#999] group-hover:text-[#f0b90b] group-hover:translate-x-1 transition-all" />
      </button>
      
      {/* Remember choice toggle */}
      <label className="flex items-center justify-center gap-2 cursor-pointer text-sm text-[#666] hover:text-[#1a1a1a] transition-colors">
        <input
          type="checkbox"
          checked={rememberChoice}
          onChange={(e) => setRememberChoice(e.target.checked)}
          className="w-4 h-4 rounded border-[#b8b3ab] text-[#f0b90b] focus:ring-[#f0b90b]"
          data-testid="remember-choice-checkbox"
        />
        Remember my choice on this device
      </label>
    </Card>
  </div>
);

// Screen 1: What is a Wallet?
const WalletIntroScreen = ({ creatorLabel, walletAddress, onContinue, onSkip }) => (
  <div className="min-h-screen bg-[#d4cfc4] flex items-center justify-center p-4">
    <Card className="w-full max-w-md bg-[#e8e3db] border-[#b8b3ab] p-6" data-testid="noob-intro-screen">
      <NoobFlowHeader creatorLabel={creatorLabel} walletAddress={walletAddress} />
      
      {/* Icon */}
      <div className="flex justify-center mb-6">
        <div className="w-20 h-20 bg-[#f0b90b]/20 rounded-full flex items-center justify-center">
          <Wallet className="h-10 w-10 text-[#f0b90b]" />
        </div>
      </div>
      
      {/* Title */}
      <h1 className="text-xl font-bold text-[#1a1a1a] text-center mb-4">
        To send a tip, you need a digital wallet.
      </h1>
      
      {/* Explanation */}
      <div className="bg-white rounded-lg p-4 mb-6 border border-[#b8b3ab]">
        <p className="text-[#666] text-sm leading-relaxed mb-4">
          A wallet is a free app that works like a secure payment app (similar to Apple Pay) — 
          it holds your TIP and lets you approve payments securely.
        </p>
        <ul className="space-y-2 text-sm text-[#1a1a1a]">
          <li className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
            No bank account needed.
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
            No monthly fees.
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
            You control your money.
          </li>
        </ul>
      </div>
      
      {/* One-time setup note */}
      <p className="text-center text-sm text-[#666] mb-6">
        <span className="font-semibold">Good news:</span> You only need to set this up once.
      </p>
      
      {/* Primary CTA */}
      <Button
        onClick={onContinue}
        className="w-full h-12 bg-[#f0b90b] hover:bg-[#d4a50a] text-white text-lg"
        data-testid="noob-intro-continue"
      >
        Continue
      </Button>
      
      {/* Skip option - subtle */}
      <button
        onClick={onSkip}
        className="w-full text-center text-[#888] text-sm mt-4 hover:text-[#1a1a1a] transition-colors"
        data-testid="noob-intro-skip"
      >
        Already have a wallet? Connect instead →
      </button>
    </Card>
  </div>
);

// Screen 2: What You'll Need
const WhatYouNeedScreen = ({ creatorLabel, walletAddress, onGetStarted, onHaveWallet, onBack }) => (
  <div className="min-h-screen bg-[#d4cfc4] flex items-center justify-center p-4">
    <Card className="w-full max-w-md bg-[#e8e3db] border-[#b8b3ab] p-6" data-testid="noob-checklist-screen">
      <NoobFlowHeader creatorLabel={creatorLabel} walletAddress={walletAddress} />
      
      <h2 className="text-xl font-bold text-[#1a1a1a] text-center mb-6">
        Here's what you'll need:
      </h2>
      
      {/* Checklist items */}
      <div className="space-y-3 mb-6">
        {/* Item 1: Wallet */}
        <div className="bg-white rounded-lg p-4 border border-[#b8b3ab]">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <CheckCircle className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="font-semibold text-[#1a1a1a]">A wallet app</p>
              <p className="text-sm text-[#666]">Free to download</p>
            </div>
          </div>
        </div>
        
        {/* Item 2: BNB */}
        <div className="bg-white rounded-lg p-4 border border-[#b8b3ab]">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <CheckCircle className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="font-semibold text-[#1a1a1a]">BNB for network fee</p>
              <p className="text-sm text-[#666]">
                About $0.10 worth (like a tiny postage stamp for the network). 
                This goes to the network — not to TipMe.
              </p>
            </div>
          </div>
        </div>
        
        {/* Item 3: TIP */}
        <div className="bg-white rounded-lg p-4 border border-[#b8b3ab]">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <CheckCircle className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="font-semibold text-[#1a1a1a]">TIP tokens</p>
              <p className="text-sm text-[#666]">The currency used for tipping</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Helper text */}
      <p className="text-center text-sm text-[#666] mb-6">
        Don't have these yet? We'll help you get started.
      </p>
      
      {/* Primary CTA */}
      <Button
        onClick={onGetStarted}
        className="w-full h-12 bg-[#f0b90b] hover:bg-[#d4a50a] text-white text-lg mb-3"
        data-testid="noob-checklist-start"
      >
        Get Started
      </Button>
      
      {/* Secondary CTA */}
      <Button
        onClick={onHaveWallet}
        variant="outline"
        className="w-full h-12 border-[#b8b3ab] text-[#1a1a1a]"
        data-testid="noob-checklist-have-wallet"
      >
        I already have a wallet
      </Button>
      
      {/* Back link */}
      <button
        onClick={onBack}
        className="w-full text-center text-[#888] text-sm mt-4 hover:text-[#1a1a1a] transition-colors flex items-center justify-center gap-1"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
    </Card>
  </div>
);

// Screen 3: Install Wallet
const InstallWalletScreen = ({ creatorLabel, walletAddress, onInstall, onBack }) => (
  <div className="min-h-screen bg-[#d4cfc4] flex items-center justify-center p-4">
    <Card className="w-full max-w-md bg-[#e8e3db] border-[#b8b3ab] p-6" data-testid="noob-install-screen">
      <NoobFlowHeader creatorLabel={creatorLabel} walletAddress={walletAddress} />
      
      <h2 className="text-xl font-bold text-[#1a1a1a] text-center mb-2">
        Step 1: Install a Wallet
      </h2>
      <p className="text-center text-[#666] text-sm mb-6">
        Choose a wallet app to install:
      </p>
      
      {/* Wallet options */}
      <div className="space-y-3 mb-6">
        {/* MetaMask - Recommended */}
        <div className="bg-white rounded-lg p-4 border-2 border-[#f0b90b]">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{WALLET_INFO.metamask.icon}</span>
              <span className="font-bold text-[#1a1a1a]">{WALLET_INFO.metamask.name}</span>
            </div>
            <span className="bg-[#f0b90b] text-white text-xs px-2 py-1 rounded-full font-semibold">
              Recommended
            </span>
          </div>
          <p className="text-sm text-[#666] mb-3">{WALLET_INFO.metamask.description}</p>
          <Button
            onClick={() => onInstall('metamask')}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="noob-install-metamask"
          >
            Install MetaMask
          </Button>
        </div>
        
        {/* Trust Wallet */}
        <div className="bg-white rounded-lg p-4 border border-[#b8b3ab]">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">{WALLET_INFO.trust.icon}</span>
            <span className="font-bold text-[#1a1a1a]">{WALLET_INFO.trust.name}</span>
          </div>
          <p className="text-sm text-[#666] mb-3">{WALLET_INFO.trust.description}</p>
          <Button
            onClick={() => onInstall('trust')}
            variant="outline"
            className="w-full border-blue-500 text-blue-600 hover:bg-blue-50"
            data-testid="noob-install-trust"
          >
            Install Trust Wallet
          </Button>
        </div>
        
        {/* Rabby - Advanced */}
        <div className="bg-gray-50 rounded-lg p-4 border border-[#b8b3ab]">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">{WALLET_INFO.rabby.icon}</span>
            <span className="font-bold text-[#666]">{WALLET_INFO.rabby.name}</span>
            <span className="bg-gray-200 text-gray-600 text-xs px-2 py-0.5 rounded-full">
              Advanced
            </span>
          </div>
          <p className="text-sm text-[#888] mb-3">{WALLET_INFO.rabby.description}</p>
          <Button
            onClick={() => onInstall('rabby')}
            variant="outline"
            className="w-full border-gray-300 text-gray-500 hover:bg-gray-100"
            data-testid="noob-install-rabby"
          >
            Visit Rabby.io
          </Button>
        </div>
      </div>
      
      {/* Footer note */}
      <p className="text-center text-sm text-[#888] mb-4">
        ℹ️ All wallets are free and safe. Pick whichever you prefer.
      </p>
      
      {/* Back link */}
      <button
        onClick={onBack}
        className="w-full text-center text-[#888] text-sm hover:text-[#1a1a1a] transition-colors flex items-center justify-center gap-1"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
    </Card>
  </div>
);

// Screen 3b: Confirm App Store Modal
// TODO: "Open App Store" text is iOS-specific. On Android it should say "Open Play Store".
//       The deep links route correctly on both platforms — this is a cosmetic-only issue.
const InstallConfirmModal = ({ wallet, onConfirm, onCancel }) => {
  const walletInfo = WALLET_INFO[wallet] || WALLET_INFO.metamask;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="noob-confirm-modal">
      <Card className="w-full max-w-sm bg-[#e8e3db] border-[#b8b3ab] p-6">
        <h3 className="text-lg font-bold text-[#1a1a1a] text-center mb-4">
          You're about to leave
        </h3>
        
        <div className="bg-white rounded-lg p-4 border border-[#b8b3ab] mb-4">
          <p className="text-[#666] text-sm mb-4">
            We'll open the {walletInfo.hasDeepLink ? 'App Store' : 'website'} so you can install <strong>{walletInfo.name}</strong>.
          </p>
          
          <p className="text-[#1a1a1a] text-sm font-semibold mb-2">After installing:</p>
          <ol className="text-sm text-[#666] space-y-1 list-decimal list-inside">
            <li>Open your new wallet</li>
            <li>Create an account (takes 2 min)</li>
            <li>Come back to this page to continue</li>
          </ol>
        </div>
        
        {/* CTAs */}
        <Button
          onClick={onConfirm}
          className="w-full h-12 bg-[#f0b90b] hover:bg-[#d4a50a] text-white mb-3"
          data-testid="noob-confirm-open-store"
        >
          Open App Store
        </Button>
        
        <Button
          onClick={onCancel}
          variant="outline"
          className="w-full h-12 border-[#b8b3ab]"
          data-testid="noob-confirm-cancel"
        >
          Cancel
        </Button>
      </Card>
    </div>
  );
};

// Screen 4: Connect Wallet (with detection)
const ConnectWalletScreen = ({ creatorLabel, walletAddress, walletDetected, onConnect, onOpenInWallet, onBack, onChangeMode, skippedWithMemory, userMode }) => (
  <div className="min-h-screen bg-[#d4cfc4] flex items-center justify-center p-4">
    <Card className="w-full max-w-md bg-[#e8e3db] border-[#b8b3ab] p-6" data-testid="noob-connect-screen">
      <NoobFlowHeader creatorLabel={creatorLabel} walletAddress={walletAddress} />
      
      {/* Change mode link - visible when entry was skipped due to remembered choice */}
      {skippedWithMemory && onChangeMode && (
        <div className="text-center mb-4">
          <button
            onClick={onChangeMode}
            className="text-xs text-[#888] hover:text-[#f0b90b] underline transition-colors"
            data-testid="change-mode-link"
          >
            Change mode ({userMode === 'advanced' ? 'Quick Tip' : 'Noob'})
          </button>
        </div>
      )}
      
      {/* Wallet Browser Context Banner */}
      <WalletContextBanner />
      
      {walletDetected ? (
        <>
          {/* Wallet Detected State */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
          </div>
          
          <h2 className="text-xl font-bold text-[#1a1a1a] text-center mb-2">
            Wallet detected!
          </h2>
          
          <p className="text-center text-[#666] text-sm mb-6">
            Tap below to connect your wallet to this page.
            This lets you approve the tip securely from your wallet app.
          </p>
          
          <Button
            onClick={onConnect}
            className="w-full h-12 bg-[#f0b90b] hover:bg-[#d4a50a] text-white text-lg mb-4"
            data-testid="noob-connect-wallet"
          >
            <Wallet className="h-5 w-5 mr-2" />
            Connect Wallet
          </Button>
          
          {/* Trust badge */}
          <p className="text-center text-xs text-[#888] mb-4">
            🔒 We never see your password or private keys.
          </p>
        </>
      ) : (
        <>
          {/* No Wallet Detected State */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center">
              <AlertCircle className="h-10 w-10 text-amber-600" />
            </div>
          </div>
          
          <h2 className="text-xl font-bold text-[#1a1a1a] text-center mb-2">
            Welcome back!
          </h2>
          
          <p className="text-center text-[#666] text-sm mb-6">
            We didn't detect a wallet yet.
            <br />If you just installed one:
          </p>
          
          {/* Deep link buttons */}
          <div className="space-y-3 mb-4">
            <Button
              onClick={() => onOpenInWallet('metamask')}
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="noob-open-metamask"
            >
              <span className="mr-2">🦊</span> Open in MetaMask
            </Button>
            
            <Button
              onClick={() => onOpenInWallet('trust')}
              className="w-full h-12 bg-blue-500 hover:bg-blue-600 text-white"
              data-testid="noob-open-trust"
            >
              <span className="mr-2">🛡️</span> Open in Trust Wallet
            </Button>
          </div>
          
          <p className="text-center text-xs text-[#888] mb-4">
            This will open your wallet's built-in browser and connect automatically.
          </p>
        </>
      )}
      
      {/* Back link */}
      <button
        onClick={onBack}
        className="w-full text-center text-[#888] text-sm hover:text-[#1a1a1a] transition-colors flex items-center justify-center gap-1"
      >
        <ArrowLeft className="h-4 w-4" /> {walletDetected ? 'Back to wallet options' : 'Still need to install? Go back'}
      </button>
    </Card>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function TipQRPage() {
  const { qrId } = useParams();
  const navigate = useNavigate();
  
  const { address, isConnected, chain, connector } = useAccount();
  const { connect, connectors } = useConnect();
  const { disconnect } = useDisconnect();
  const { switchChain } = useSwitchChain();
  const { openConnectModal } = useConnectModal();
  const { isReconnecting, isPWA, connectWithFlag } = usePWAReconnect(openConnectModal);
  
  // Balance checks
  const { data: bnbBalance, refetch: refetchBnb } = useBalance({ 
    address, 
    chainId: BSC_CHAIN_ID 
  });
  const { data: tipBalance, refetch: refetchTip } = useBalance({ 
    address, 
    token: TIP_TOKEN_ADDRESS,
    chainId: BSC_CHAIN_ID 
  });
  
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tipAmount, setTipAmount] = useState('');
  const [tipUsdPrice, setTipUsdPrice] = useState(0);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Noob flow states (mobile W0+T0)
  const [noobStep, setNoobStep] = useState(null); // 'intro' | 'checklist' | 'install' | 'connect' | null
  const [showInstallConfirmModal, setShowInstallConfirmModal] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [walletDetected, setWalletDetected] = useState(false);
  const [showEntryChoice, setShowEntryChoice] = useState(false); // Entry choice screen
  const [userMode, setUserMode] = useState(null); // 'advanced' | 'beginner' (session state)
  const [rememberChoice, setRememberChoice] = useState(true); // Remember toggle (default ON)
  const [skippedEntryWithMemory, setSkippedEntryWithMemory] = useState(false); // Track if we skipped due to memory
  
  const { writeContract, data: txHash, isPending: isSending, error: sendError } = useWriteContract();
  const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({ hash: txHash });
  
  // Android WalletConnect chooser hint
  const isAndroid = /Android/i.test(navigator.userAgent || '');
  const [chooserHintDismissed, setChooserHintDismissed] = useState(
    () => localStorage.getItem('tipme_android_chooser_hint_dismissed') === 'true'
  );
  const wcPeerNameRef = useRef(null);
  
  // Read WC peer wallet name once when sending starts
  useEffect(() => {
    if (isSending && isAndroid && connector?.id === 'walletConnect' && !wcPeerNameRef.current) {
      (async () => {
        try {
          const provider = await connector.getProvider();
          wcPeerNameRef.current = provider?.session?.peer?.metadata?.name || null;
        } catch {}
      })();
    }
  }, [isSending, isAndroid, connector]);
  
  const dismissChooserHint = useCallback(() => {
    setChooserHintDismissed(true);
    localStorage.setItem('tipme_android_chooser_hint_dismissed', 'true');
  }, []);
  
  const PRESET_AMOUNTS = ['10', '50', '100', '500'];
  
  // Debug flag - set via console: localStorage.setItem('TIPME_DEBUG', 'true')
  const DEBUG = typeof window !== 'undefined' && localStorage.getItem('TIPME_DEBUG') === 'true';
  
  // Calculate balance states
  const bnbValue = parseFloat(bnbBalance?.formatted || '0');
  const tipValue = parseFloat(tipBalance?.formatted || '0');
  const hasBNB = bnbValue >= MIN_BNB_FOR_GAS;
  const hasTIP = tipValue >= MIN_TIP_TO_SEND;
  
  // Debug logging for balance tracking
  useEffect(() => {
    if (DEBUG && isConnected) {
      console.log('[TIPME DEBUG] Balance update:', {
        bnbBalance: bnbBalance?.formatted,
        tipBalance: tipBalance?.formatted,
        bnbValue,
        tipValue,
        hasBNB,
        hasTIP,
        needsOnboarding,
        isRefreshing
      });
    }
  }, [bnbBalance, tipBalance, bnbValue, tipValue, hasBNB, hasTIP, needsOnboarding, isRefreshing, isConnected, DEBUG]);
  
  // Initialize userMode from localStorage on mount (only if remembered)
  useEffect(() => {
    const savedMode = localStorage.getItem(STORAGE_KEYS.userMode);
    if (savedMode) {
      setUserMode(savedMode);
      setSkippedEntryWithMemory(true); // Mark that we used remembered choice
    }
  }, []);
  
  // IFRAME PROTECTION: This page requires wallet connection and MUST run as full page
  useEffect(() => {
    if (window.top !== window.self) {
      // We're inside an iframe - try to break out to full page
      console.warn('[TipQR] Detected iframe context - attempting to escape');
      try {
        window.top.location.href = window.location.origin + `/tip/${qrId}`;
      } catch (e) {
        // Cross-origin iframe - can't escape, but tip page can still work
        // Just log the warning and continue
        console.warn('[TipQR] Cannot escape iframe (cross-origin):', e.message);
      }
    }
  }, [qrId]);
  
  // Fetch TIP token USD price for display
  useEffect(() => {
    async function fetchTipPrice() {
      try {
        const res = await fetch(`${API_URL}/api/tip-price`);
        if (res.ok) {
          const data = await res.json();
          if (data.price && data.price > 0) setTipUsdPrice(data.price);
        }
      } catch { /* price display is optional */ }
    }
    fetchTipPrice();
  }, []);
  
  // Resolve QR and initialize noob flow
  useEffect(() => {
    async function resolveQR() {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/api/qr/${qrId}`);
        if (!res.ok) {
          setError(res.status === 404 ? 'QR_NOT_FOUND' : 'Failed to load');
          return;
        }
        const data = await res.json();
        setQrData(data);
        
        // Mobile pipeline — routes users through the appropriate flow
        if (isMobile() && !isConnected) {
          const hasWallet = detectWalletInstalled();
          const insideWallet = isInsideWalletBrowser();
          const hadPrevConnection = Boolean(localStorage.getItem('wagmi.store'));
          setWalletDetected(hasWallet || insideWallet);
          
          // Branch 1: Inside wallet browser → skip all, go to connect
          if (insideWallet) {
            setNoobStep('connect');
            return;
          }
          
          // Branch 2: Returning from wallet app install → go to connect
          const installStarted = localStorage.getItem(STORAGE_KEYS.installStarted);
          if (installStarted) {
            localStorage.removeItem(STORAGE_KEYS.installStarted);
            setNoobStep('connect');
            return;
          }
          
          // Branch 3: Remembered mode from previous session → route by mode
          const savedMode = localStorage.getItem(STORAGE_KEYS.userMode);
          if (savedMode) {
            setUserMode(savedMode);
            setSkippedEntryWithMemory(true);
            if (savedMode === 'advanced') {
              setNoobStep('connect');
            } else {
              const seenIntro = localStorage.getItem(STORAGE_KEYS.introSeen);
              setNoobStep(seenIntro ? (hasWallet ? 'connect' : 'checklist') : 'intro');
            }
            return;
          }
          
          // Branch 4: No remembered mode, but wagmi session from a previous visit
          //           → user connected before, treat as experienced, go to connect
          if (hadPrevConnection) {
            setNoobStep('connect');
            return;
          }
          
          // Branch 5: First time, no remembered choice, no previous connection
          //           → show Entry Choice screen (Quick Tip vs I'm New)
          setShowEntryChoice(true);
        }
      } catch (err) {
        setError('Failed to load QR data');
      } finally {
        setLoading(false);
      }
    }
    if (qrId) resolveQR();
  }, [qrId, isConnected]);
  
  // REACTIVE: Check if user needs onboarding (missing BNB or TIP)
  // This effect watches balance data directly and updates needsOnboarding state
  useEffect(() => {
    if (!isConnected || !address) {
      setNeedsOnboarding(false);
      return;
    }
    
    // Compute current balance states from fresh data
    const currentBnbValue = parseFloat(bnbBalance?.formatted || '0');
    const currentTipValue = parseFloat(tipBalance?.formatted || '0');
    const currentHasBNB = currentBnbValue >= MIN_BNB_FOR_GAS;
    const currentHasTIP = currentTipValue >= MIN_TIP_TO_SEND;
    
    // User needs onboarding if they're missing BNB for gas OR TIP tokens
    const shouldNeedOnboarding = !currentHasBNB || !currentHasTIP;
    
    if (DEBUG) {
      console.log('[TIPME DEBUG] Onboarding check:', {
        currentBnbValue,
        currentTipValue,
        currentHasBNB,
        currentHasTIP,
        shouldNeedOnboarding,
        previousNeedsOnboarding: needsOnboarding
      });
    }
    
    setNeedsOnboarding(shouldNeedOnboarding);
  }, [isConnected, address, bnbBalance?.formatted, tipBalance?.formatted, DEBUG]);
  
  // Reset noob step when connected
  useEffect(() => {
    if (isConnected) {
      setNoobStep(null);
    }
  }, [isConnected]);
  
  const handleRefreshBalances = useCallback(async () => {
    if (DEBUG) {
      console.log('[TIPME DEBUG] handleRefreshBalances called');
    }
    
    setIsRefreshing(true);
    
    try {
      // Refetch both balances - the useEffect watching balances will handle the state update
      const [bnbResult, tipResult] = await Promise.all([refetchBnb(), refetchTip()]);
      
      if (DEBUG) {
        console.log('[TIPME DEBUG] Refetch results:', {
          bnbResult: bnbResult?.data?.formatted,
          tipResult: tipResult?.data?.formatted
        });
      }
      
      // Check balances from the refetch results directly
      const newBnbValue = parseFloat(bnbResult?.data?.formatted || '0');
      const newTipValue = parseFloat(tipResult?.data?.formatted || '0');
      const newHasBNB = newBnbValue >= MIN_BNB_FOR_GAS;
      const newHasTIP = newTipValue >= MIN_TIP_TO_SEND;
      
      if (DEBUG) {
        console.log('[TIPME DEBUG] After refetch - Balance check:', {
          newBnbValue,
          newTipValue,
          newHasBNB,
          newHasTIP,
          willNeedOnboarding: !newHasBNB || !newHasTIP
        });
      }
      
      // The useEffect will handle updating needsOnboarding based on the new balance data
      // But we also handle immediate feedback here to avoid race condition
      if (newHasBNB && newHasTIP) {
        setNeedsOnboarding(false);
        if (DEBUG) {
          console.log('[TIPME DEBUG] User has sufficient balances - exiting onboarding');
        }
      } else {
        if (DEBUG) {
          console.log('[TIPME DEBUG] User still needs:', {
            needsBNB: !newHasBNB,
            needsTIP: !newHasTIP,
            message: !newHasBNB && !newTipValue ? 'Both BNB and TIP missing' :
                     !newHasBNB ? 'BNB for gas still missing' : 'TIP tokens still missing'
          });
        }
      }
    } catch (err) {
      console.error('[TIPME ERROR] Balance refresh failed:', err);
    } finally {
      setIsRefreshing(false);
    }
  }, [refetchBnb, refetchTip, DEBUG]);
  
  // ===== ENTRY CHOICE HANDLERS =====
  
  const handleQuickTip = useCallback(() => {
    // Advanced user: save choice if remember is ON, skip all onboarding, go to connect
    if (rememberChoice) {
      localStorage.setItem(STORAGE_KEYS.userMode, 'advanced');
    }
    setUserMode('advanced');
    setShowEntryChoice(false);
    setSkippedEntryWithMemory(false); // User just made fresh choice
    setWalletDetected(detectWalletInstalled() || isInsideWalletBrowser());
    setNoobStep('connect');
  }, [rememberChoice]);
  
  const handleNoob = useCallback(() => {
    // Beginner user: save choice if remember is ON, start full onboarding
    if (rememberChoice) {
      localStorage.setItem(STORAGE_KEYS.userMode, 'beginner');
    }
    setUserMode('beginner');
    setShowEntryChoice(false);
    setSkippedEntryWithMemory(false); // User just made fresh choice
    setNoobStep('intro');
  }, [rememberChoice]);
  
  // Change mode - clears remembered choice and shows entry screen again
  const handleChangeMode = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.userMode);
    localStorage.removeItem(STORAGE_KEYS.introSeen);
    setUserMode(null);
    setNoobStep(null);
    setSkippedEntryWithMemory(false);
    setShowEntryChoice(true);
  }, []);
  
  // ===== NOOB FLOW HANDLERS =====
  
  const handleNoobIntroContinue = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.introSeen, 'true');
    setNoobStep('checklist');
  }, []);
  
  const handleNoobIntroSkip = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.introSeen, 'true');
    setNoobStep('connect');
    setWalletDetected(detectWalletInstalled() || isInsideWalletBrowser());
  }, []);
  
  const handleNoobGetStarted = useCallback(() => {
    setNoobStep('install');
  }, []);
  
  const handleNoobHaveWallet = useCallback(() => {
    setWalletDetected(detectWalletInstalled() || isInsideWalletBrowser());
    setNoobStep('connect');
  }, []);
  
  const handleNoobInstallWallet = useCallback((walletType) => {
    setSelectedWallet(walletType);
    setShowInstallConfirmModal(true);
  }, []);
  
  const handleNoobConfirmInstall = useCallback(() => {
    if (selectedWallet) {
      // Mark that install was started for return detection
      localStorage.setItem(STORAGE_KEYS.installStarted, selectedWallet);
      setShowInstallConfirmModal(false);
      
      // Redirect to app store or website
      if (selectedWallet === 'rabby') {
        window.open('https://rabby.io', '_blank');
      } else {
        window.location.href = DEEP_LINKS[selectedWallet](window.location.href);
      }
    }
  }, [selectedWallet]);
  
  const handleNoobOpenInWallet = useCallback((walletType) => {
    window.location.href = DEEP_LINKS[walletType](window.location.href);
  }, []);
  
  const handleNoobBack = useCallback((targetStep) => {
    setNoobStep(targetStep);
  }, []);
  
  // ===== ORIGINAL HANDLERS =====
  
  const handleDeepLink = useCallback((walletType) => {
    window.location.href = DEEP_LINKS[walletType](window.location.href);
  }, []);
  
  const handleWalletConnect = useCallback(async () => {
    const wcConnector = connectors.find(c => c.id === 'walletConnect');
    if (wcConnector) {
      await connect({ connector: wcConnector });
      setNoobStep(null);
    }
  }, [connect, connectors]);
  
  const handleDirectConnect = useCallback(async () => {
    // Set PWA flag before opening modal — iOS may kill the app during wallet switch
    setPWAConnecting();
    // Always show RainbowKit wallet selection modal — never auto-pick a wallet
    if (openConnectModal) {
      openConnectModal();
      setNoobStep(null);
    }
    // No fallback to injected connector — user must choose their wallet
  }, [openConnectModal]);
  
  const handleSendTip = useCallback(async () => {
    if (!tipAmount || !qrData?.wallet || !isConnected) return;
    
    if (chain?.id !== BSC_CHAIN_ID) {
      try { await switchChain({ chainId: BSC_CHAIN_ID }); } 
      catch (err) { return; }
    }
    
    try {
      const amountWei = parseUnits(tipAmount, 18);
      writeContract({
        address: TIP_TOKEN_ADDRESS,
        abi: TIP_TOKEN_ABI,
        functionName: 'transfer',
        args: [qrData.wallet, amountWei],
        chainId: BSC_CHAIN_ID
      });
    } catch (err) {
      console.error('Send error:', err);
    }
  }, [tipAmount, qrData, isConnected, chain, switchChain, writeContract]);
  
  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-[#d4cfc4] flex items-center justify-center p-4">
        <Loader2 className="h-12 w-12 animate-spin text-[#f0b90b]" />
      </div>
    );
  }
  
  // PWA Reconnecting — show spinner while restoring wallet session
  if (!loading && !isConnected && isReconnecting) {
    return (
      <div className="min-h-screen bg-[#d4cfc4] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-[#e8e3db] border-[#b8b3ab] p-8 text-center" data-testid="pwa-reconnecting">
          <Loader2 className="h-12 w-12 animate-spin text-[#f0b90b] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#1a1a1a] mb-2">Finishing connection...</h2>
          <p className="text-[#666] text-sm">Restoring your wallet session</p>
        </Card>
      </div>
    );
  }
  
  // Error
  if (error) {
    // Detect if the qrId is actually a wallet address (0x...)
    const isWalletAddress = qrId && /^0x[a-fA-F0-9]{40}$/.test(qrId);
    
    return (
      <div className="min-h-screen bg-[#d4cfc4] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-[#e8e3db] border-[#b8b3ab] p-8 text-center">
          {isWalletAddress ? (
            <>
              <QrCode className="h-16 w-16 text-[#f0b90b] mx-auto mb-4" />
              <h2 className="text-xl font-bold text-[#1a1a1a] mb-2">No QR Code Yet</h2>
              <p className="text-[#666] mb-6">This creator hasn't set up their tip QR yet. Create one to start receiving tips!</p>
              <Button onClick={() => navigate('/register-qr', { replace: true })} className="bg-[#f0b90b] hover:bg-[#d4a50a] text-white" data-testid="create-qr-redirect">
                <QrCode className="h-4 w-4 mr-2" /> Create Your QR Code
              </Button>
            </>
          ) : (
            <>
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-[#1a1a1a] mb-2">
                {error === 'QR_NOT_FOUND' ? 'QR Code Not Found' : 'Error'}
              </h2>
              <p className="text-[#666] mb-6">
                {error === 'QR_NOT_FOUND' ? 'This QR code is not active.' : error}
              </p>
              <Button onClick={() => navigate('/')} className="bg-[#f0b90b] hover:bg-[#d4a50a] text-white">
                <ArrowLeft className="h-4 w-4 mr-2" /> Go Home
              </Button>
            </>
          )}
        </Card>
      </div>
    );
  }
  
  // Success
  if (isConfirmed) {
    return (
      <div className="min-h-screen bg-[#d4cfc4] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-[#e8e3db] border-[#b8b3ab] p-8 text-center">
          <CheckCircle className="h-16 w-16 text-[#22c55e] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#1a1a1a] mb-2">Tip Sent!</h2>
          <p className="text-[#666] mb-2">You sent {tipAmount} TIP to {qrData?.label || 'Creator'}</p>
          <a
            href={`https://bscscan.com/tx/${txHash}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#f0b90b] hover:underline flex items-center justify-center gap-1 mb-4"
          >
            View on BscScan <ExternalLink className="h-4 w-4" />
          </a>
          <p className="text-[#888] text-sm mb-6">Returning to dashboard...</p>
          <AutoRedirect to="/dashboard" delay={2000} navigate={navigate} />
          <Button onClick={() => navigate('/dashboard', { replace: true })} className="bg-[#f0b90b] hover:bg-[#d4a50a] text-white">
            Go to Dashboard
          </Button>
        </Card>
        <PWAInstallBanner show={true} />
      </div>
    );
  }
  
  // ============================================================================
  // ENTRY CHOICE SCREEN (Mobile - First time users)
  // ============================================================================
  
  if (showEntryChoice && !isConnected) {
    return (
      <EntryChoiceScreen
        creatorLabel={qrData?.label}
        walletAddress={qrData?.wallet}
        onQuickTip={handleQuickTip}
        onNoob={handleNoob}
        rememberChoice={rememberChoice}
        setRememberChoice={setRememberChoice}
      />
    );
  }
  
  // ============================================================================
  // NOOB FLOW SCREENS (Mobile W0+T0)
  // ============================================================================
  
  // Screen 1: What is a Wallet?
  if (noobStep === 'intro') {
    return (
      <>
        <WalletIntroScreen
          creatorLabel={qrData?.label}
          walletAddress={qrData?.wallet}
          onContinue={handleNoobIntroContinue}
          onSkip={handleNoobIntroSkip}
        />
        {showInstallConfirmModal && (
          <InstallConfirmModal
            wallet={selectedWallet}
            onConfirm={handleNoobConfirmInstall}
            onCancel={() => setShowInstallConfirmModal(false)}
          />
        )}
      </>
    );
  }
  
  // Screen 2: What You'll Need
  if (noobStep === 'checklist') {
    return (
      <>
        <WhatYouNeedScreen
          creatorLabel={qrData?.label}
          walletAddress={qrData?.wallet}
          onGetStarted={handleNoobGetStarted}
          onHaveWallet={handleNoobHaveWallet}
          onBack={() => handleNoobBack('intro')}
        />
        {showInstallConfirmModal && (
          <InstallConfirmModal
            wallet={selectedWallet}
            onConfirm={handleNoobConfirmInstall}
            onCancel={() => setShowInstallConfirmModal(false)}
          />
        )}
      </>
    );
  }
  
  // Screen 3: Install Wallet
  if (noobStep === 'install') {
    return (
      <>
        <InstallWalletScreen
          creatorLabel={qrData?.label}
          walletAddress={qrData?.wallet}
          onInstall={handleNoobInstallWallet}
          onBack={() => handleNoobBack('checklist')}
        />
        {showInstallConfirmModal && (
          <InstallConfirmModal
            wallet={selectedWallet}
            onConfirm={handleNoobConfirmInstall}
            onCancel={() => setShowInstallConfirmModal(false)}
          />
        )}
      </>
    );
  }
  
  // Screen 4: Connect Wallet
  if (noobStep === 'connect') {
    return (
      <>
        <ConnectWalletScreen
          creatorLabel={qrData?.label}
          walletAddress={qrData?.wallet}
          walletDetected={walletDetected}
          onConnect={handleDirectConnect}
          onOpenInWallet={handleNoobOpenInWallet}
          onBack={() => handleNoobBack('install')}
          onChangeMode={handleChangeMode}
          skippedWithMemory={skippedEntryWithMemory}
          userMode={userMode}
        />
        {showInstallConfirmModal && (
          <InstallConfirmModal
            wallet={selectedWallet}
            onConfirm={handleNoobConfirmInstall}
            onCancel={() => setShowInstallConfirmModal(false)}
          />
        )}
      </>
    );
  }
  
  // ============================================================================
  // END NOOB FLOW SCREENS
  // ============================================================================
  
  // Onboarding Guide (shows ONLY for BEGINNER users who need BNB or TIP)
  // Advanced users (Quick Tip) skip this and see inline warnings on tip interface
  if (isConnected && needsOnboarding && userMode === 'beginner') {
    return (
      <div className="min-h-screen bg-[#d4cfc4] flex items-center justify-center p-4">
        <OnboardingGuide 
          hasBNB={hasBNB}
          hasTIP={hasTIP}
          onRefresh={handleRefreshBalances}
          isRefreshing={isRefreshing}
          address={address}
        />
      </div>
    );
  }
  
  // Main Tip Interface
  return (
    <div className="min-h-screen bg-[#d4cfc4] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-[#e8e3db] border-[#b8b3ab] p-6" data-testid="tip-card">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-[#1a1a1a] mb-1" data-testid="recipient-label">Tip {qrData?.label || 'Creator'}</h2>
          <p className="text-[#666] font-mono text-xs" data-testid="recipient-wallet">{qrData?.wallet?.slice(0, 6)}...{qrData?.wallet?.slice(-4)}</p>
          
          {/* Change mode link - visible when entry was skipped due to remembered choice */}
          {skippedEntryWithMemory && isMobile() && !isConnected && (
            <button
              onClick={handleChangeMode}
              className="text-xs text-[#888] hover:text-[#f0b90b] underline mt-2 transition-colors"
              data-testid="change-mode-link"
            >
              Change mode ({userMode === 'advanced' ? 'Quick Tip' : 'Noob'})
            </button>
          )}
        </div>
        
        {!isConnected ? (
          <div className="text-center">
            <p className="text-[#666] mb-4">Connect your wallet to send a tip</p>
            <Button
              onClick={() => {
                // Set PWA flag before opening modal
                setPWAConnecting();
                // Always open wallet chooser modal — never auto-select
                if (openConnectModal) {
                  openConnectModal();
                } else {
                  handleDirectConnect();
                }
              }}
              className="w-full bg-[#f0b90b] hover:bg-[#d4a50a] text-white"
              data-testid="connect-wallet-btn"
            >
              <Wallet className="h-4 w-4 mr-2" /> Connect Wallet
            </Button>
          </div>
        ) : (
          <>
            {/* Connected wallet */}
            <div className="bg-[#d4cfc4] rounded-lg p-3 mb-4" data-testid="connected-wallet-info">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#666]">Connected</p>
                  <p className="font-mono text-sm text-[#1a1a1a]" data-testid="user-wallet">{address?.slice(0, 6)}...{address?.slice(-4)}</p>
                </div>
                <button onClick={() => disconnect()} className="text-sm text-[#666] hover:text-[#1a1a1a] underline" data-testid="disconnect-btn">Disconnect</button>
              </div>
            </div>
            
            {/* Balances */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              <div className="bg-[#d4cfc4] rounded-lg p-2 text-center" data-testid="bnb-balance">
                <p className="text-xs text-[#666]">BNB</p>
                <p className="font-semibold text-[#1a1a1a]">{parseFloat(bnbBalance?.formatted || '0').toFixed(4)}</p>
              </div>
              <div className="bg-[#d4cfc4] rounded-lg p-2 text-center" data-testid="tip-balance">
                <p className="text-xs text-[#666]">TIP</p>
                <p className="font-semibold text-[#f0b90b]">{parseFloat(tipBalance?.formatted || '0').toFixed(2)}</p>
              </div>
            </div>
            
            {/* Inline warnings for advanced users missing tokens */}
            {userMode === 'advanced' && (!hasBNB || !hasTIP) && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4" data-testid="advanced-token-warning">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    {!hasBNB && !hasTIP && (
                      <p className="text-amber-700">You need BNB (for gas) and TIP tokens to send a tip.</p>
                    )}
                    {!hasBNB && hasTIP && (
                      <p className="text-amber-700">You need a small amount of BNB for the transaction fee.</p>
                    )}
                    {hasBNB && !hasTIP && (
                      <p className="text-amber-700">You need TIP tokens to send a tip. <a href={`https://pancakeswap.finance/swap?outputCurrency=${TIP_TOKEN_ADDRESS}&inputCurrency=BNB`} target="_blank" rel="noopener noreferrer" className="underline font-semibold">Swap on PancakeSwap →</a></p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Chain warning */}
            {chain?.id !== BSC_CHAIN_ID && (
              <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 mb-4" data-testid="wrong-network-warning">
                <div className="flex items-center gap-2 text-amber-700 mb-2">
                  <AlertCircle className="h-4 w-4" />
                  <span className="font-semibold text-sm">Wrong Network</span>
                </div>
                <Button onClick={() => switchChain({ chainId: BSC_CHAIN_ID })} size="sm" className="w-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="switch-network-btn">
                  Switch to BSC
                </Button>
              </div>
            )}
            
            {/* Preset amounts */}
            <div className="mb-4">
              <p className="text-sm text-[#666] mb-2">Quick amounts (TIP)</p>
              <div className="grid grid-cols-4 gap-2" data-testid="preset-amounts">
                {PRESET_AMOUNTS.map((amount) => (
                  <Button
                    key={amount}
                    variant={tipAmount === amount ? "default" : "outline"}
                    size="sm"
                    onClick={() => setTipAmount(amount)}
                    className={tipAmount === amount ? "bg-[#f0b90b] hover:bg-[#d4a50a] text-white" : "border-[#b8b3ab] text-white hover:bg-[#333]"}
                    data-testid={`preset-amount-${amount}`}
                  >
                    {amount}
                    {tipUsdPrice > 0 && (
                      <span className="block text-[10px] opacity-60">${(parseFloat(amount) * tipUsdPrice).toFixed(2)}</span>
                    )}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Custom amount */}
            <div className="mb-4">
              <p className="text-sm text-[#666] mb-2">Custom amount (TIP)</p>
              <Input
                type="number"
                placeholder="0.00"
                value={tipAmount}
                onChange={(e) => setTipAmount(e.target.value)}
                className="bg-[#d4cfc4] border-[#b8b3ab] text-lg"
                data-testid="custom-amount-input"
              />
              {tipAmount && parseFloat(tipAmount) > 0 && tipUsdPrice > 0 && (
                <p className="text-sm mt-1.5 text-right" style={{ color: '#888' }} data-testid="usd-equivalent">
                  ≈ ${(parseFloat(tipAmount) * tipUsdPrice).toFixed(2)} USD
                </p>
              )}
              {tipAmount && parseFloat(tipAmount) > 0 && tipUsdPrice === 0 && (
                <p className="text-xs mt-1.5 text-right" style={{ color: '#555' }}>
                  USD price unavailable
                </p>
              )}
            </div>
            
            {/* Send button */}
            <Button
              onClick={handleSendTip}
              disabled={!tipAmount || parseFloat(tipAmount) <= 0 || isSending || isConfirming || chain?.id !== BSC_CHAIN_ID}
              className="w-full h-12 bg-[#f0b90b] hover:bg-[#d4a50a] text-white disabled:opacity-50 text-lg"
              data-testid="send-tip-btn"
            >
              {isSending || isConfirming ? (
                <><Loader2 className="h-5 w-5 mr-2 animate-spin" />{isSending ? 'Confirm in Wallet...' : 'Confirming on chain...'}</>
              ) : (
                <>Send {tipAmount || '0'} TIP{tipAmount && parseFloat(tipAmount) > 0 && tipUsdPrice > 0 ? ` (~$${(parseFloat(tipAmount) * tipUsdPrice).toFixed(2)})` : ''}</>
              )}
            </Button>
            
            {/* Reassurance message during wallet interaction */}
            {isSending && (
              <div className="mt-3 rounded-lg p-3 text-center" style={{ backgroundColor: 'rgba(240,185,11,0.1)', border: '1px solid rgba(240,185,11,0.2)' }} data-testid="wallet-patience-msg">
                <p className="text-xs font-medium" style={{ color: '#f0b90b' }}>Your wallet app is opening</p>
                <p className="text-xs mt-1" style={{ color: '#888' }}>This can take a few seconds — please approve the transaction in your wallet to complete the tip.</p>
                {isAndroid && connector?.id === 'walletConnect' && !chooserHintDismissed && (
                  <div
                    onClick={dismissChooserHint}
                    className="mt-2 flex items-center justify-between rounded px-2.5 py-1.5 cursor-pointer"
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}
                    data-testid="android-chooser-hint"
                  >
                    <p className="text-xs text-left" style={{ color: '#aaa' }}>
                      Tip: select <strong style={{ color: '#e8e3db' }}>{wcPeerNameRef.current || 'your wallet'}</strong> and tap "Always" to skip this prompt next time
                    </p>
                    <span className="ml-2 text-xs flex-shrink-0" style={{ color: '#666' }}>✕</span>
                  </div>
                )}
              </div>
            )}
            {isConfirming && (
              <div className="mt-3 rounded-lg p-3 text-center" style={{ backgroundColor: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }} data-testid="chain-patience-msg">
                <p className="text-xs font-medium" style={{ color: '#22c55e' }}>Transaction submitted!</p>
                <p className="text-xs mt-1" style={{ color: '#888' }}>Waiting for the blockchain to confirm — usually takes 3-5 seconds.</p>
              </div>
            )}
            
            {/* Error */}
            {sendError && (
              <div className="mt-4 bg-red-100 border border-red-300 rounded-lg p-3 text-red-700 text-sm" data-testid="send-error">
                <p className="font-semibold mb-1">Transaction Failed</p>
                <p className="text-xs">{sendError.shortMessage || sendError.message}</p>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
